import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import {
  FiAward,
  FiGift,
  FiShoppingBag,
  FiStar,
  FiCheckCircle,
  FiArrowRight,
  FiTrendingUp,
  FiZap,
  FiClock,
} from "react-icons/fi";
import { useUser } from "../../contextApi/userContext";
import axios from "axios";
import API_URL from "../../../config";

const Wallet = () => {
  const [currentPoints, setCurrentPoints] = useState(100);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [points, setPoints] = useState();
  const [activeTab, setActiveTab] = useState("earn");
  const { userDetails, setUserDetails, logout } = useUser();

  useEffect(() => {
    const storedUser = localStorage.getItem("userDetails");
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    setUserDetails(parsedUser);

    if (parsedUser) {
      const rewardHistory = JSON.parse(
        localStorage.getItem("blogRewardHistory") || "{}"
      );

      // Safely get pending points with fallback to 0
      const pendingPoints = Number(rewardHistory?.pendingPoints) || 0;

      // First fetch current points from server
      axios
        .get(`${API_URL}/layout/get-wallet-details?id=${parsedUser.id}`, {
          headers: {
            Authorization: `Bearer ${parsedUser.token}`,
          },
        })
        .then((res) => {
          if (res?.data) {
            const serverPoints = Number(res.data.rewardPoints) || 0;
            const totalPoints = serverPoints + pendingPoints;

            // Update local state immediately
            setPoints(totalPoints);

            // Only update server if there were pending points
            if (pendingPoints > 0) {
              return axios.post(
                `${API_URL}/layout/add-points-to-wallet`,
                {
                  userId: parsedUser.id,
                  blog_id: rewardHistory?.lastBlogId,
                  points: pendingPoints, 
                  read_date: rewardHistory?.lastRewardDate || 0,
                },
                {
                  headers: {
                    Authorization: `Bearer ${parsedUser.token}`,
                  },
                }
              );
            }
          }
        })
        .then((updateResponse) => {
          // This will only execute if pendingPoints > 0 and update was successful
          if (pendingPoints > 0) {
            // Clear pending points after successful update
            const updatedHistory = { ...rewardHistory, pendingPoints: 0 };
            localStorage.setItem(
              "blogRewardHistory",
              JSON.stringify(updatedHistory)
            );
            console.log("Pending points successfully updated in DB");
          }
        })
        .catch((err) => {
          console.error("Failed to update points:", err);
          // If update fails, keep pending points in localStorage for next attempt
        });
    }
  }, []);

  console.log("points", points);

  // Sample data
  const waysToEarn = [
    {
      id: 1,
      title: "Complete Profile",
      points: 100,
      completed: true,
      icon: <FiCheckCircle />,
    },
    {
      id: 2,
      title: "Read a blog",
      points: 50,
      completed: false,
      icon: <FiClock />,
      link: "/blog",
    },
    {
      id: 3,
      title: "Refer a Friend",
      points: 200,
      completed: false,
      icon: <FiShoppingBag />,
    },
    {
      id: 4,
      title: "Book a service",
      points: 10,
      completed: false,
      icon: <FiShoppingBag />,
    },
    {
      id: 5,
      title: "Take a Quiz",
      points: 75,
      completed: false,
      icon: <FiStar />,
    },
  ];

  const rewardProducts = [
    {
      id: 1,
      name: "Premium Subscription",
      points: 500,
      image: "https://source.unsplash.com/random/300x200?premium",
    },
    {
      id: 2,
      name: "Discount Coupon 10%",
      points: 300,
      image: "https://source.unsplash.com/random/300x200?coupon",
    },
    {
      id: 3,
      name: "Exclusive E-book",
      points: 700,
      image: "https://source.unsplash.com/random/300x200?ebook",
    },
    {
      id: 4,
      name: "Free Shipping",
      points: 200,
      image: "https://source.unsplash.com/random/300x200?shipping",
    },
    {
      id: 5,
      name: "VIP Badge",
      points: 1000,
      image: "https://source.unsplash.com/random/300x200?vip",
    },
  ];

  const nextLevelPoints = 2000;
  const progressPercentage = Math.min(
    (currentPoints / nextLevelPoints) * 100,
    100
  );

  const handleRedeem = (product) => {
    if (currentPoints >= product.points) {
      setSelectedProduct(product);
      setShowRedeemModal(true);
    }
  };

  const confirmRedeem = () => {
    setCurrentPoints((prev) => prev - selectedProduct.points);
    setShowRedeemModal(false);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="container py-4">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-4"
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div className="d-flex align-items-center gap-2">
            <FiAward size={24} />
            <h2 className="mb-0">My Rewards</h2>
          </div>
        </div>

        <p className="text-muted">Earn points and unlock exclusive benefits</p>

        <div className="py-3 justify-content-center align-items-center">
          <div className="bg-light">
            <span className="fw-bold fs-2">Your Points</span>
            <div className="d-flex align-items-center gap-2 fs-2 fw-bold">
              <CountUp end={points} duration={1.5} separator="," />
              <FiStar className="text-warning" />
            </div>
          </div>
        </div>
      </motion.header>

      {/* Tabs */}
      <div className="btn-group mb-4 w-100 shadow-sm">
        <button
          className={`btn ${
            activeTab === "earn" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setActiveTab("earn")}
        >
          <FiTrendingUp className="me-2" />
          Earn Points
        </button>
        <button
          className={`btn ${
            activeTab === "redeem" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setActiveTab("redeem")}
        >
          <FiGift className="me-2" />
          Redeem Rewards
        </button>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: activeTab === "earn" ? -20 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: activeTab === "earn" ? -20 : 20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === "earn" ? (
            <div>
              <h4 className="mb-3">
                <FiTrendingUp className="me-2" />
                Ways to Earn
              </h4>
              <div className="row">
                {waysToEarn.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="col-md-6 col-lg-4 mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div
                      className={`card shadow-sm h-100 ${
                        item.completed ? "border-success" : "border-warning"
                      }`}
                    >
                      <div className="card-body d-flex flex-column align-items-start">
                        <div className="mb-2 fs-4 text-primary">
                          {item.icon}
                        </div>
                        <h5 className="card-title">{item.title}</h5>
                        <p className="text-muted">+{item.points} points</p>
                        <span
                          className={`badge ${
                            item.completed
                              ? "bg-success"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {item.completed ? "Completed" : "Pending"}
                        </span>
                        <a href={item?.link}>Collect points</a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <h4 className="mb-3">
                <FiGift className="me-2" />
                Available Rewards
              </h4>
              <div className="row">
                {rewardProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    className="col-md-6 col-lg-4 mb-3"
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div
                      className={`card shadow-sm h-100 ${
                        currentPoints >= product.points
                          ? "border-primary"
                          : "border-secondary"
                      }`}
                    >
                      <div
                        className="card-img-top bg-light d-flex align-items-center justify-content-center"
                        style={{
                          height: 150,
                          backgroundImage: `url(${product.image})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      >
                        {currentPoints < product.points && (
                          <div className="bg-dark bg-opacity-50 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white">
                            <FiZap size={24} />
                            <span>
                              Need {product.points - currentPoints} more pts
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="card-body">
                        <h5 className="card-title">{product.name}</h5>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">{product.points} pts</span>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            disabled={currentPoints < product.points}
                            onClick={() => handleRedeem(product)}
                          >
                            Redeem <FiArrowRight />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {showRedeemModal && (
          <motion.div
            className="modal fade show d-block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRedeemModal(false)}
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <motion.div
              className="modal-dialog modal-dialog-centered"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FiGift className="me-2" />
                    Confirm Redemption
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowRedeemModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="fs-5 fw-bold">{selectedProduct?.name}</p>
                  <ul className="list-unstyled">
                    <li className="d-flex justify-content-between">
                      <span>Current Points:</span>
                      <span>{currentPoints}</span>
                    </li>
                    <li className="d-flex justify-content-between">
                      <span>Cost:</span>
                      <span>-{selectedProduct?.points}</span>
                    </li>
                    <hr />
                    <li className="d-flex justify-content-between fw-bold">
                      <span>Remaining:</span>
                      <span>
                        {currentPoints - (selectedProduct?.points || 0)}
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowRedeemModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={confirmRedeem}>
                    Confirm
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Wallet;
